import {
  ADVANCE_METHODS,
  EXPENSE_CATEGORIES,
  isTraceable,
  pickableExpenseCategories,
  PAYMENT_WAYS,
  paymentOf,
} from '@console/shared-kernel';
import { RotateCcw, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatAmount } from '../money';
import {
  DOCUMENT_TYPES,
  holdsProposal,
  isRequiredByType,
  methodPickFor,
  type ExpenseForm,
  type ProposableField,
  type Proposals,
} from './form-model';
import { NativeSelect } from './NativeSelect';
import { CardPicker } from './ReferencePickers';

export type FieldKey = Exclude<
  keyof ExpenseForm,
  | 'cardLabel'
  | 'vehicleLabel'
  | 'supplierId'
  | 'siteId'
  | 'stayPropertySiteId'
  | 'stayPropertyLabel'
>;
export type FieldOptions = {
  proposable?: boolean;
  /** The control has its own ✕ (the supplier picker): the shell does not add a second one. */
  ownClear?: boolean;
  className?: string | undefined;
  group?: boolean;
  /** Whether the control shows anything, when the form value alone cannot tell (a half-typed date). */
  filled?: boolean;
};

type PaymentWayOption = (typeof PAYMENT_WAYS)[number];

/** The payment choices by who paid; a way in none of the lists falls into «other», never hidden. */
const PAYMENT_GROUPS: readonly {
  key: 'company' | 'personal';
  ways: readonly PaymentWayOption[];
}[] = [
  { key: 'company', ways: ['company-card', 'company-transfer'] },
  { key: 'personal', ways: ['personal-card', 'transfer', 'cash'] },
];

function groupPaymentWays(): { key: 'company' | 'personal' | 'other'; ways: PaymentWayOption[] }[] {
  const listed = new Set<PaymentWayOption>(PAYMENT_GROUPS.flatMap((group) => group.ways));
  return [
    ...PAYMENT_GROUPS.map((group) => ({ key: group.key, ways: [...group.ways] })),
    { key: 'other', ways: [...PAYMENT_WAYS.filter((way) => !listed.has(way))] },
  ];
}

type ControlProps = {
  readonly uid: string;
  readonly form: ExpenseForm;
  readonly set: (patch: Partial<ExpenseForm>) => void;
};

function PaymentWayRadios({ uid, form, set }: ControlProps) {
  const { t } = useTranslation('expenses');
  const way = form.paymentWay;
  return (
    <div role="radiogroup" aria-labelledby={`${uid}-paymentWay-label`} className="space-y-2">
      {groupPaymentWays().map((group) => (
        <div key={group.key} className="space-y-1">
          <div aria-hidden="true" className="text-xs text-muted-foreground">
            {t(`capture.paymentGroups.${group.key}`)}
          </div>
          <div className="flex flex-wrap gap-2">
            {group.ways.map((option) => (
              <label
                key={option}
                className={cn(
                  'flex min-h-[38px] cursor-pointer items-center gap-2 rounded-full border border-input px-3 text-sm',
                  way === option && 'border-ring bg-selected text-selected-foreground',
                )}
              >
                <input
                  type="radio"
                  name={`${uid}-paymentWay`}
                  value={option}
                  checked={way === option}
                  onChange={() => set({ paymentWay: option, cardId: '', cardLabel: '' })}
                />
                {t(`capture.paymentWays.${option}`)}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdvanceMethodRadios({ uid, form, set }: ControlProps) {
  const { t } = useTranslation('expenses');
  return (
    <div
      role="radiogroup"
      aria-label={t('capture.fields.advanceMethod')}
      className="flex flex-wrap items-center gap-3 text-sm"
    >
      <span className="text-muted-foreground">{t('capture.fields.advanceMethod')}</span>
      {ADVANCE_METHODS.map((method) => (
        <label key={method} className="flex items-center gap-1.5">
          <input
            type="radio"
            name={`${uid}-advanceMethod`}
            value={method}
            checked={form.advanceMethod === method}
            onChange={() => set({ advanceMethod: method, cardId: '', cardLabel: '' })}
          />
          {t(`capture.advanceMethods.${method}`)}
        </label>
      ))}
    </div>
  );
}

/** The payment control: who paid, the director's advance method, the card, and the notes. */
export function PaymentControl({
  uid,
  form,
  set,
  listOwner,
}: ControlProps & { readonly listOwner: string | undefined }) {
  const { t } = useTranslation('expenses');
  const way = form.paymentWay;
  const paid = way === '' ? null : paymentOf(way, form.advanceMethod);
  const methodPick = methodPickFor(form);
  return (
    <div className="space-y-2">
      <PaymentWayRadios uid={uid} form={form} set={set} />
      {way === 'director-advance' && <AdvanceMethodRadios uid={uid} form={form} set={set} />}
      {methodPick !== null && (
        <CardPicker
          owner={listOwner}
          pick={methodPick}
          value={form.cardId}
          storedLabel={form.cardLabel}
          onChange={(cardId, cardLabel) => set({ cardId, cardLabel })}
        />
      )}
      {paid && !isTraceable(paid.paymentMethod) && (
        <p role="note" className="rounded-md bg-warning-tint p-2 text-sm text-warning">
          {t('capture.payment.untraceable')}
        </p>
      )}
      {(paid?.payer === 'company-card' || paid?.payer === 'company-transfer') && (
        <p role="note" className="rounded-md bg-muted p-2 text-sm text-muted-foreground">
          {t('capture.payment.companyCard')}
        </p>
      )}
    </div>
  );
}

/** The categories a person picks by hand: a server-managed one (the allowance) never shows. */
const PICKABLE_CATEGORIES = pickableExpenseCategories();

export function CategorySelect({
  uid,
  form,
  set,
  allowed,
}: ControlProps & { readonly allowed?: ReadonlySet<string> | null | undefined }) {
  const { t, i18n } = useTranslation('expenses');
  const english = i18n.language.startsWith('en');
  const category = EXPENSE_CATEGORIES.find((c) => c.code === form.category);
  return (
    <>
      <NativeSelect
        id={`${uid}-category`}
        value={form.category}
        aria-describedby={category ? `${uid}-category-hint` : undefined}
        onChange={(e) => set({ category: e.target.value })}
      >
        <option value="">{t('capture.fields.categoryPlaceholder')}</option>
        {PICKABLE_CATEGORIES.filter((c) => !allowed || allowed.has(c.code)).map((c) => (
          <option key={c.code} value={c.code}>
            {english ? c.labelEn : c.labelIt}
          </option>
        ))}
      </NativeSelect>
      {category && (
        <p id={`${uid}-category-hint`} className="mt-1 text-xs text-muted-foreground">
          {category.hintIt}
        </p>
      )}
    </>
  );
}

export function DocumentTypeSelect({ uid, form, set }: ControlProps) {
  const { t } = useTranslation('expenses');
  return (
    <NativeSelect
      id={`${uid}-documentType`}
      value={form.documentType}
      onChange={(e) => set({ documentType: e.target.value as ExpenseForm['documentType'] })}
    >
      <option value="">{t('capture.fields.none')}</option>
      {DOCUMENT_TYPES.map((type) => (
        <option key={type} value={type}>
          {t(`capture.documentTypes.${type}`)}
        </option>
      ))}
    </NativeSelect>
  );
}

export function MileageTotalField({
  uid,
  total,
}: {
  readonly uid: string;
  readonly total: string | null;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="min-w-0 space-y-1 sm:col-span-2" data-field="mileageTotal">
      <div className="flex min-h-11 items-center">
        <label htmlFor={`${uid}-mileageTotal`} className="text-sm font-medium">
          {t('capture.fields.mileageTotal')}
        </label>
      </div>
      <Input
        id={`${uid}-mileageTotal`}
        readOnly
        className="bg-muted tabular-nums"
        value={total === null ? '—' : formatAmount(total)}
      />
    </div>
  );
}

function ProposalMarks({
  label,
  proposal,
  holds,
  onRestore,
}: {
  readonly label: string;
  readonly proposal: Proposals[ProposableField];
  readonly holds: boolean;
  readonly onRestore: () => void;
}) {
  const { t } = useTranslation('expenses');
  const { t: tDetail } = useTranslation('expenses-detail');
  return (
    <>
      {holds && (
        <Sparkles
          role="img"
          aria-label={t('capture.prefill.proposed')}
          className="size-3.5 text-brand"
        />
      )}
      {holds && proposal?.uncertain && (
        <span className="rounded bg-warning-tint px-1 text-xs">
          {t('capture.prefill.uncertain')}
        </span>
      )}
      {holds && proposal && !proposal.uncertain && (
        <span className="rounded bg-muted px-1 text-xs">{tDetail('ai.sure')}</span>
      )}
      {proposal && !holds && (
        <>
          <span className="text-xs text-muted-foreground">{t('capture.prefill.modified')}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 min-w-11 px-1"
            aria-label={t('capture.prefill.restore', { field: label })}
            onClick={onRestore}
          >
            <RotateCcw />
          </Button>
        </>
      )}
    </>
  );
}

export type FieldShellProps = {
  readonly fieldKey: FieldKey;
  readonly uid: string;
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  /** The required field is empty and was left once: its error shows. */
  readonly showError: boolean;
  readonly control: ReactNode;
  readonly options: FieldOptions;
  readonly onRestore: (field: ProposableField) => void;
  readonly onClear: (key: FieldKey) => void;
};

/** A field's label row and control, with the proposal's marks and controls when it has one. */
export function FieldShell(props: FieldShellProps) {
  const { fieldKey: key, uid, form, proposals, showError, control, options } = props;
  const { t } = useTranslation('expenses');
  const required = isRequiredByType(form, key);
  const label = t(`capture.fields.${key}`);
  const proposable = options.proposable ? (key as ProposableField) : null;
  const proposal = proposable ? proposals[proposable] : undefined;
  const holds = proposable ? holdsProposal(form, proposable, proposals) : false;
  const filled = options.filled ?? (typeof form[key] === 'string' && form[key] !== '');
  const errorKey = key === 'referenceDays' ? 'nightsError' : 'requiredError';
  return (
    <div className={cn('min-w-0 space-y-1', options.className)} data-field={key}>
      <div className="flex min-h-11 items-center gap-1">
        {options.group ? (
          <span id={`${uid}-${key}-label`} className="text-sm font-medium">
            {label}
          </span>
        ) : (
          <label htmlFor={`${uid}-${key}`} className="text-sm font-medium">
            {label}
          </label>
        )}
        {required && (
          <b className="text-[11.5px] font-semibold text-muted-foreground">
            {t('capture.fields.required')}
          </b>
        )}
        <ProposalMarks
          label={label}
          proposal={proposal}
          holds={holds}
          onRestore={() => props.onRestore(proposable!)}
        />
        <span className="grow" />
        {proposable && filled && !options.ownClear && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 px-2 text-muted-foreground"
            aria-label={t('capture.prefill.clear', { field: label })}
            onClick={() => props.onClear(key)}
          >
            {t('capture.prefill.clearText')}
          </Button>
        )}
      </div>
      <div
        className={cn('rounded-md', holds && proposal?.uncertain && 'p-1.5 ring-2 ring-warning')}
      >
        {control}
      </div>
      {required && showError && (
        <p id={`${uid}-${key}-error`} className="text-xs text-destructive">
          {t(`capture.fields.${errorKey}`)}
        </p>
      )}
    </div>
  );
}
